/* eslint-disable arrow-body-style */
import { React, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { DataTable } from 'components';
import clsx from 'clsx';
import moment from 'moment';
import { IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import { ToggleSiteTrackerDialog, RemoveSiteEntry, GetReportEmail } from '../../actions/SiteTrackerActions';

const useStyles = makeStyles((theme) => ({
	root: {},
	content: {
		padding: 0,
	},
	inner: {
		minWidth: 700,
	},
	nameCell: {
		display: 'flex',
		alignItems: 'center',
	},
	avatar: {
		height: 42,
		width: 42,
		marginRight: theme.spacing(1),
	},
	actions: {
		padding: theme.spacing(1),
		justifyContent: 'flex-end',
	},
	primaryButton: {
		color: theme.palette.primary.main
	},
	errorButton: {
		color: theme.palette.error.main
	},
	infoButton: {
		color: theme.palette.info.main
	}
}));

const SiteTrackerTable = (props) => {
	const { className, data, ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();
	const user = useSelector(state => state.session.user);
	const [open, setOpen] = useState(false);
	const [siteTrackerIdToRemove, setSiteTrackerIdToRemove] = useState(null);

	const _onEditSiteTrackerClick = (siteTrackerId) => {
		dispatch(ToggleSiteTrackerDialog(true, siteTrackerId));
	};

	const _onRemoveSiteTrackerClick = () => {
		if (siteTrackerIdToRemove !== null) {
			dispatch(RemoveSiteEntry(siteTrackerIdToRemove, user.id));
			// Reset the state after removal
			setSiteTrackerIdToRemove(null);
		}
		setOpen(false);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleClickOpen = (siteTrackerId) => {
		// Set the siteTrackerId to be removed when the dialog is opened
		setSiteTrackerIdToRemove(siteTrackerId);
		setOpen(true);
	};

	const _onReportEmailClick = (siteTrackerId) => {
		dispatch(GetReportEmail(siteTrackerId, user.id));
	};

	const _handleStyle = (value) => {
		if (value === 'To Validate - BIS Report' || value === 'To Validate - TTO Report') {
			return {
				style: {
					backgroundColor: 'yellow',
					color: 'black',
					fontWeight: 'bold'
				}
			};
		}
		return {
		};
	};

	const _renderActions = (tableMeta) => {
		const { rowData } = tableMeta;
		return (
			<>
				<Tooltip title={tableMeta.rowData[12] === 'Complete' ? 'View' : 'Edit'}>
					<IconButton edge="end" size="small" className={classes.primaryButton} onClick={() => _onEditSiteTrackerClick(rowData[0])}>
						{tableMeta.rowData[12] === 'Complete' ? <ManageSearchIcon /> : <EditIcon />}
					</IconButton>
				</Tooltip>
				{user.role === 'Manager' && (
					<Tooltip title="Remove">
						<IconButton edge="end" size="small" className={classes.errorButton} onClick={() => handleClickOpen(rowData[0])}>
							<CancelIcon />
						</IconButton>
					</Tooltip>
				)}
				{(rowData[12] === 'BIS Report Done' || rowData[12] === 'TTO Report Submit' || rowData[12] === 'Complete') && (
					<Tooltip title="Report">
						<IconButton edge="end" size="small" className={classes.primaryButton} onClick={() => _onReportEmailClick(rowData[0])}>
							<MailOutlineIcon />
						</IconButton>
					</Tooltip>
				)}
			</>
		);
	};

	const _getTableColumns = () => (
		[
			{
				name: 'id',
				label: 'Id',
				options: {
					display: false,
					filter: false,
					viewColumns: false,
					filterType: 'textField',
				}
			},
			{
				name: 'key',
				label: 'Key',
				options: {
					display: false,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Key - ${v}`
					},
				}
			},
			{
				name: 'lcs',
				label: 'LCS',
				options: {
					display: true,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `LCS - ${v}`
					},
				}
			},
			{
				name: 'bsh',
				label: 'BSH',
				options: {
					display: true,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `BSH - ${v}`
					},
				}
			},
			{
				name: 'siteType',
				label: 'Site Type',
				options: {
					display: true,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `Site Type - ${v}`
					},
				}
			},
			{
				name: 'siteName',
				label: 'Site Name',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Site Name - ${v}`
					},
				}
			},
			{
				name: 'dasZone',
				label: 'DAS Zone',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `DAS Zone - ${v}`
					},
				}
			},
			{
				name: 'activationDate',
				label: 'Activation Date',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Activation Date - ${v}`
					},
					customBodyRender: (value, tableMeta, updateValue) => <>{value === null ? '' : moment(value).format('YYYY-MM-DD').toString()}</>
				}
			},
			{
				name: 'activationStatus',
				label: 'Activation Status',
				options: {
					display: true,
					filterType: 'multiselect',
					customFilterListOptions: {
						render: v => `Activation Status - ${v}`
					},
					filterList: ['Complete', 'Scheduled'],
				}
			},
			{
				name: 'sow',
				label: 'SOW',
				options: {
					display: true,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `SOW - ${v}`
					},
				}
			},
			{
				name: 'comments',
				label: 'Comments',
				options: {
					display: false,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Comments - ${v}`
					},
				}
			},
			{
				name: 'actionStatus',
				label: 'Action Status',
				options: {
					display: true,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `Action Status - ${v}`
					},
					setCellProps: (value) => (
						_handleStyle(value)
					)
				}
			},
			{
				name: 'currentStatus',
				label: 'Current Status',
				options: {
					display: true,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `Current Status - ${v}`
					},
				}
			},
			{
				name: 'd1ReportOwner',
				label: 'D1 Report Owner',
				options: {
					display: false,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `D1 Report Owner - ${v}`
					},
				}
			},
			{
				name: 'd1ReportStatusDate',
				label: 'D1 Report Status Date',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `D1 Report Status Date - ${v}`
					},
					customBodyRender: (value, tableMeta, updateValue) => <>{value === null ? '' : moment(value).format('YYYY-MM-DD').toString()}</>
				}
			},
			{
				name: 'd1PCVReportComments',
				label: 'D1 PCV Report Comments',
				options: {
					display: false,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `D1 PCV Report Comments - ${v}`
					},
				}
			},
			{
				name: 'd1BISReportComments',
				label: 'D1 BIS Report Comments',
				options: {
					display: false,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `D1 BIS Report Comments - ${v}`
					},
				}
			},
			{
				name: 'd1Status',
				label: 'D1 Status',
				options: {
					display: true,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `D1 Status - ${v}`
					},
				}
			},
			{
				name: 'd7ReportOwner',
				label: 'D7 Report Owner',
				options: {
					display: false,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `D7 Report Owner - ${v}`
					},
				}
			},
			{
				name: 'd7ReportStatusDate',
				label: 'D7 Report Status Date',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `D7 Report Status Date - ${v}`
					},
					customBodyRender: (value, tableMeta, updateValue) => <>{value === null ? '' : moment(value).format('YYYY-MM-DD').toString()}</>
				}
			},
			{
				name: 'd7PCVReportComments',
				label: 'D7 PCV Report Comments',
				options: {
					display: false,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `D7 PCV Report Comments - ${v}`
					},
				}
			},
			{
				name: 'd7BISReportComments',
				label: 'D7 BIS Report Comments',
				options: {
					display: false,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `D7 BIS Report Comments - ${v}`
					},
				}
			},
			{
				name: 'd7Status',
				label: 'D7 Status',
				options: {
					display: true,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `D7 Status - ${v}`
					},
				}
			},
			{
				name: 'ttoSubmitDate',
				label: 'TTO Submit Date',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `TTO Submit Date - ${v}`
					},
					customBodyRender: (value, tableMeta, updateValue) => <>{value === null ? '' : moment(value).format('YYYY-MM-DD').toString()}</>
				}
			},
			{
				name: 'ttoApprovedDate',
				label: 'TTO Approved Date',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `TTO Approved Date - ${v}`
					},
					customBodyRender: (value, tableMeta, updateValue) => <>{value === null ? '' : moment(value).format('YYYY-MM-DD').toString()}</>
				}
			},
			{
				name: 'action',
				label: 'Actions',
				options: {
					filter: false,
					sort: false,
					viewColumns: false,
					empty: true,
					customBodyRender: (value, tableMeta, updateValue) => _renderActions(tableMeta)
				}
			},
		]
	);

	return (
		<div {...rest} className={clsx(classes.root, className)}>
			<DataTable
				id="siteDataTable"
				title=""
				data={data}
				columns={_getTableColumns()}
				rowsPerPage={20}
				tableBodyMaxHeight="30em"
				tableBodyCellMaxWidth="250"
			/>
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">
					Please Confirm Site Entry Remove
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						Keep in mind that
						<br />
						<br />
						<h2>ALL DATA WILL BE LOST!</h2>
						<br />
						Are you sure you want to remove this entry?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>CANCEL</Button>
					<Button onClick={_onRemoveSiteTrackerClick}>REMOVE</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

SiteTrackerTable.propTypes = {
	className: PropTypes.string,
	data: PropTypes.array.isRequired,
};

export default SiteTrackerTable;
